import { z } from 'zod'
import { isCountryUSOrCA, validateEmail, validatePhone } from '@utils/validation'
import { validateCardNumber, validateCardExpiry, validateCardCvc, validateCardholderName } from '@utils/cardValidation'
import { detectCardBrand } from './checkoutHelpers'

// Card schema — matches PaymentUpdater's validation logic exactly.
// Uses the shared cardValidation utilities so the two checkout paths stay in sync.
const cardSchema = z
  .object({
    title: z.string(),
    number: z.string(),
    expiry: z.string(),
    cvv: z.string(),
  })
  .superRefine((data, ctx) => {
    const nameResult = validateCardholderName(data.title)
    if (!nameResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: nameResult.error || 'Invalid cardholder name',
        path: ['title'],
      })
    }
    const numberResult = validateCardNumber(data.number)
    if (!numberResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: numberResult.error || 'Invalid card number',
        path: ['number'],
      })
    }
    const expiryResult = validateCardExpiry(data.expiry)
    if (!expiryResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: expiryResult.error || 'Invalid expiration date',
        path: ['expiry'],
      })
    }
    const brand = detectCardBrand(data.number)
    const cvvResult = validateCardCvc(data.cvv, brand)
    if (!cvvResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: cvvResult.error || 'Invalid security code',
        path: ['cvv'],
      })
    }
  })
type CardFormValues = z.infer<typeof cardSchema>

const customerSchema = z
  .object({
    first_name: z
      .string()
      .min(2, 'First name must be at least 2 characters')
      .regex(/^[a-zA-Z\s'-]+$/, 'First name contains invalid characters'),
    last_name: z
      .string()
      .min(2, 'Last name must be at least 2 characters')
      .regex(/^[a-zA-Z\s'-]+$/, 'Last name contains invalid characters'),
    company: z.string().optional().default(''),
    email: z
      .string()
      .min(1, 'Email is required')
      .refine(
        v => validateEmail(v).isValid,
        v => ({ message: validateEmail(v).error || 'Invalid email' })
      ),
    phone: z
      .string()
      .min(1, 'Phone is required')
      .refine(
        v => validatePhone(v).isValid,
        v => ({ message: validatePhone(v).error || 'Invalid phone number' })
      ),
    address1: z.string().min(2, 'Address is required'),
    address2: z.string().optional().default(''),
    city: z.string().min(2, 'City is required'),
    state: z.string().optional().default(''),
    zip: z
      .string()
      .min(3, 'ZIP code is required')
      .regex(/^[a-zA-Z0-9\s-]+$/, 'Invalid ZIP code'),
    country: z.string().min(1, 'Country is required'),
    ship_first_name: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'First name must be at least 2 characters'),
    ship_last_name: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'Last name must be at least 2 characters'),
    ship_address1: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'Address is required'),
    ship_address2: z.string().optional().default(''),
    ship_city: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'City is required'),
    ship_state: z.string().optional().default(''),
    ship_zip: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || /^[a-zA-Z0-9\s-]*$/.test(v), 'Invalid ZIP code'),
    ship_country: z.string().optional().default('US'),
  })
  .superRefine((data, ctx) => {
    if (isCountryUSOrCA(data.country) && (!data.state || !data.state.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'State is required', path: ['state'] })
    }
    if (
      data.ship_country &&
      isCountryUSOrCA(data.ship_country) &&
      data.ship_address1 &&
      (!data.ship_state || !data.ship_state.trim())
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'State is required', path: ['ship_state'] })
    }
  })
type CustomerFormValues = z.infer<typeof customerSchema>

export { cardSchema, customerSchema }
export type { CardFormValues, CustomerFormValues }
